/**
 * SanctionsReviews — Risk Management clears or confirms held sanctions screenings (Bill 2026-11-02).
 *
 * A screening with a match or a potential match holds the application (person ofacStatus 'hit-current'). Here Risk
 * compares each listing with the applicant, as OFAC recommends (Assessing OFAC Name Matches, FAQ 5), and records:
 *   Cleared   — not the listed party. The application is released; later screenings of the same person on the same
 *               application that find only these listings stay clear.
 *   Confirmed — a true match. The hold stays.
 * A reason is required. Whoever ran the screening cannot decide it. Server: SanctionsReviewService.
 */
import { useCallback, useEffect, useState } from 'react';
import { ExternalLink } from '@retail-ops/panel-shell';

import { fetchReportPdf } from '@/services/lexisNexisReportsApi';
import {
  decideReview,
  loadReviews,
  type ReviewDecisionKind,
  type ReviewView,
  type SanctionsCandidate,
  type SanctionsReviewItem,
} from '@/services/sanctionsApi';
import { formatLocal } from '@/utils/time';

const ROLE_LABEL: Record<string, string> = { decide: 'Match or review', review: 'Holds for review', off: 'Off' };
const BAND_LABEL: Record<string, string> = { hit: 'Match', review: 'Potential match', 'cleared-by-detail': 'Cleared by date of birth or country' };
const MIN_REASON = 10;

function compare(listed: ReadonlyArray<string>, applicant: string | null, outcome: string | null, same: string): string {
  const l = listed.length > 0 ? listed.join(', ') : '—';
  const a = applicant ?? 'none given';
  const o = outcome === same ? (same === 'same-year' ? 'within a year' : 'same') : outcome === 'different' ? 'different' : 'not compared';
  return `${l} · applicant: ${a} (${o})`;
}

function contextLabel(c: string): string {
  if (c === 'person-dialog') return 'Screened by the officer';
  if (c.startsWith('rescreen') || c === 'commit-rescreen') return 'Rescreened automatically';
  return c;
}

function Listing({ c, item }: { c: SanctionsCandidate; item: SanctionsReviewItem }): JSX.Element {
  return (
    <div style={{ border: '1px solid var(--surface-border)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{c.primaryName}</strong>
        <span className={c.band === 'hit' ? 'pill pill--err' : 'pill pill--warn'}>{BAND_LABEL[c.band] ?? c.band}</span>
        <span>Score {c.score}</span>
        <span style={{ color: 'var(--ink-500)' }}>
          {c.sourceLabel} · {ROLE_LABEL[c.role] ?? c.role} · entry {c.sourceEntryId}
        </span>
      </div>
      <div style={{ fontSize: 13, marginTop: 4 }}>
        <div>Matched on: {c.matchedName}{c.matchedKind && c.matchedKind !== 'primary' ? ` (${c.matchedKind})` : ''}{c.weakAlias ? ', weak alias' : ''}</div>
        <div>Program(s): {c.programs.length > 0 ? c.programs.join(', ') : '—'}</div>
        <div>Date(s) of birth: {compare(c.birthDates, item.applicantBirthYear?.toString() ?? null, c.dobComparison, 'same-year')}</div>
        <div>Country(ies): {compare(c.countries, item.applicantCountry, c.countryComparison, 'same')}</div>
      </div>
    </div>
  );
}

function OpenItem({ item, onDecided }: { item: SanctionsReviewItem; onDecided: () => void }): JSX.Element {
  const [decision, setDecision] = useState<ReviewDecisionKind | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openReport = async (): Promise<void> => {
    setError(null);
    try {
      const { url } = await fetchReportPdf('sanctions', item.screeningId);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not print the report.');
    }
  };

  const submit = async (): Promise<void> => {
    if (!decision) return;
    setBusy(true);
    setError(null);
    try {
      await decideReview(item.screeningId, decision, reason.trim());
      onDecided();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The decision was not saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h2>
          {item.nameScreened}{' '}
          <span className={item.verdict === 'hit' ? 'pill pill--err' : 'pill pill--warn'}>
            {item.verdict === 'hit' ? 'Match' : 'Potential match'}
          </span>
          {item.testApplication && <span className="pill pill--muted" style={{ marginLeft: 8 }}>Test application</span>}
        </h2>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void openReport()}>
          <ExternalLink size={14} /> Sanctions Screening report
        </button>
      </div>
      <p className="form-hint">
        {item.decision?.decision === 'escalated' && (
          <span className="banner banner--info" style={{ display: 'block', marginBottom: 6 }}>
            Sent to Risk Management by {item.decision.decidedBy}, {formatLocal(item.decision.decidedAt)}: {item.decision.reason}
          </span>
        )}
        {item.verdict === 'hit' && item.decision?.decision !== 'escalated' && (
          <span className="banner banner--err" style={{ display: 'block', marginBottom: 6 }}>
            A Match (92 or more on an OFAC list) comes straight to Risk Management for a second review.
          </span>
        )}
        {contextLabel(item.context)} {formatLocal(item.screenedAt)}
        {item.screenedBy ? ` by ${item.screenedBy}` : ''} · branch {item.branch ?? '—'} · officer {item.officerCode ?? '—'} ·
        application {item.cartId.slice(0, 8)}
      </p>

      {item.candidates.filter((c) => c.band === 'hit' || c.band === 'review').map((c) => (
        <Listing key={`${c.sourceId}-${c.sourceEntryId}`} c={c} item={item} />
      ))}
      {item.forecast.filter((f) => f.assessment !== 'also-our-match').map((f) => (
        <p key={f.aliasId} className="form-hint">
          Core Director forecast hit: {f.aliasName} ({f.assessment}). {f.explanation}
        </p>
      ))}

      <fieldset style={{ border: 'none', padding: 0, margin: '12px 0 0' }}>
        <legend className="form-field__label">Decision</legend>
        <label style={{ display: 'block', marginBottom: 4 }}>
          <input type="radio" name={`d-${item.screeningId}`} checked={decision === 'cleared'} onChange={() => setDecision('cleared')} />{' '}
          <strong>Clear</strong>: not the listed party. Releases the application.
        </label>
        <label style={{ display: 'block' }}>
          <input type="radio" name={`d-${item.screeningId}`} checked={decision === 'confirmed'} onChange={() => setDecision('confirmed')} />{' '}
          <strong>Confirm match</strong>: the applicant is the listed party. The application stays held.
        </label>
      </fieldset>
      <label className="form-field">
        <span className="form-field__label">Reason (what you compared and what you found)</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={2000} />
      </label>
      {error && <div className="banner banner--err" role="alert">{error}</div>}
      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || !decision || reason.trim().length < MIN_REASON}
        onClick={() => void submit()}
      >
        {busy ? 'Saving…' : 'Record decision'}
      </button>
    </section>
  );
}

export function SanctionsReviews(): JSX.Element {
  const [view, setView] = useState<ReviewView>('open');
  const [items, setItems] = useState<ReadonlyArray<SanctionsReviewItem> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setItems(null);
    setError(null);
    try {
      setItems(await loadReviews(view));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the reviews.');
      setItems([]);
    }
  }, [view]);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <>
      <section className="card">
        <div className="card-header"><h2>Held sanctions screenings</h2></div>
        <p className="form-hint">
          Each application here is held because a name on it scored as a match or a potential match. Compare every listing
          with the applicant (other names, date of birth, nationality, identification numbers, address), as OFAC recommends,
          then clear it (not the listed party) or confirm the match. Your decision and reason print on the Sanctions
          Screening report. You cannot decide a screening you ran.
        </p>
        <div className="action-row">
          <button type="button" className={view === 'open' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={() => setView('open')}>
            Waiting for a decision
          </button>
          <button type="button" className={view === 'decided' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={() => setView('decided')}>
            Recent decisions
          </button>
          <button type="button" className={view === 'officers' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={() => setView('officers')}>
            With officers
          </button>
        </div>
      </section>

      {error && <section className="banner banner--err" role="alert">{error}</section>}
      {items === null && <p className="empty">Loading…</p>}
      {items !== null && items.length === 0 && !error && (
        <p className="empty">{view === 'open' ? 'No applications are waiting for a sanctions decision.' : view === 'officers' ? 'No held results are with officers.' : 'No decisions yet.'}</p>
      )}

      {view === 'open' && items?.map((i) => <OpenItem key={i.screeningId} item={i} onDecided={() => void reload()} />)}

      {view === 'officers' && items && items.length > 0 && (
        <section className="card">
          <p className="form-hint">
            Held potential matches the officer is deciding (Risk settings: officer first). They come to you only if the
            officer sends them, or if you change the setting.
          </p>
          <table className="table">
            <thead><tr><th>Screened</th><th>Name</th><th>Highest score</th><th>Branch</th><th>Officer</th></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.screeningId}>
                  <td>{formatLocal(i.screenedAt)}</td><td>{i.nameScreened}</td><td>{i.topScore}</td><td>{i.branch ?? '—'}</td><td>{i.officerCode ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {view === 'decided' && items && items.length > 0 && (
        <section className="card">
          <table className="table">
            <thead>
              <tr><th>Decided</th><th>Name</th><th>Decision</th><th>By</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.screeningId}>
                  <td>{i.decision ? formatLocal(i.decision.decidedAt) : '—'}</td>
                  <td>{i.nameScreened}</td>
                  <td>
                    <span className={i.decision?.decision === 'cleared' ? 'pill pill--ok' : 'pill pill--err'}>
                      {i.decision?.decision === 'cleared' ? 'Cleared' : 'Confirmed match'}
                      {i.decision?.decidedAs === 'officer' ? ' (officer)' : ' (Risk)'}
                    </span>
                  </td>
                  <td>{i.decision?.decidedBy ?? '—'}</td>
                  <td>{i.decision?.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
